import { QueryClient } from "@tanstack/react-query";
import { httpClient, HttpError } from "@coworker/http-client";
import {
  Article,
  Articles,
  ArticleWithMediaCount,
  EdsData,
  PartialArticle,
  SalesStopData,
} from "../types/article";
import { Ids } from "../types/images";
import { QueryKeys } from "../hooks/queryKeys";
import {
  ImageUsageEntry,
  ImageUsageByMediaResponse,
} from "../types/imageUsage";

export const getArticleById = async (articleId: string) => {
  try {
    return await httpClient.roomsettings.get<Article>(`/articles/${articleId}`);
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const updateArticle = async (
  articleId: string,
  article: PartialArticle
) => {
  await httpClient.roomsettings.put(`/articles/${articleId}`, {
    body: article,
    responseType: "text",
  });
};

export const addArticle = async (
  article: Partial<Article>,
  queryClient?: QueryClient
) => {
  delete article.id;
  const data = await httpClient.roomsettings.post<Ids>("/articles/single", {
    body: article,
  });
  await invalidateArticlesRelatedQueries(queryClient);
  return data;
};

export const addArticlesBulk = async (
  articles: Partial<Article>[],
  groupId: string,
  queryClient?: QueryClient
) => {
  articles.forEach((article) => {
    delete article.id;
  });
  const data = await httpClient.roomsettings.post<Articles>(
    `/articles${groupId ? `?groupId=${groupId}` : ""}`,
    { body: articles }
  );
  await invalidateArticlesRelatedQueries(queryClient);
  return data;
};

export const deleteArticleById = async (
  articleId: string,
  queryClient: QueryClient
) => {
  await httpClient.roomsettings.delete(`/articles/${articleId}`);
  await invalidateArticlesRelatedQueries(queryClient);
};

export const deleteArticleByIds = async (
  articleIds: string[],
  queryClient?: QueryClient
) => {
  await httpClient.roomsettings.delete(
    `/articles?ids=${articleIds.join(",")}`,
    {
      responseType: "text",
    }
  );
  await invalidateArticlesRelatedQueries(queryClient);
};

export const getEDSData = async (countryId: string, articleNumbers: string) => {
  try {
    const data = await httpClient.data.get<EdsData[]>(
      "/eds/getEdsOnArticlesByCountryId",
      {
        params: {
          countryId,
          articleNumbers,
        },
      }
    );
    return data || [];
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const getSalesStopData = async (
  storeId: string,
  articleNumbers: string
) => {
  try {
    return await httpClient.data.get<SalesStopData[]>(
      "/sales-stops/getSalesStops",
      {
        params: {
          storeId,
          articleNumbers,
        },
      }
    );
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return [];
};

export const searchArticlesInMedias = async (
  storeId: string,
  searchQuery: string,
  hfb?: string
) => {
  try {
    const params: Record<string, string> = {};
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery) params["searchQuery"] = trimmedQuery;
    if (hfb) params["hfb"] = hfb;

    return await httpClient.roomsettings.get<ArticleWithMediaCount[]>(
      `/articles/search/${encodeURIComponent(storeId)}`,
      { params }
    );
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const getArticlesByHfb = async (storeId: string, hfb: string) => {
  try {
    return await httpClient.roomsettings.get<ArticleWithMediaCount[]>(
      `/articles/hfb/${encodeURIComponent(storeId)}`,
      { params: { hfb } }
    );
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const getCombinationArticleData = async (subArticleId: string) => {
  try {
    return await httpClient.roomsettings.get<Article>(
      `/articles/combinations`,
      { params: { subArticleId } }
    );
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return {};
};

export async function syncCombinationArticles(
  {
    toAdd,
    toUpdate,
    toDelete,
  }: {
    toAdd: Partial<Article>[];
    toUpdate: Article[];
    toDelete: { id: string }[];
  },
  queryClient?: QueryClient
) {
  if (toAdd.length === 0 && toUpdate.length === 0 && toDelete.length === 0) {
    return; // nothing to sync
  }

  const data = await httpClient.roomsettings.post<Articles>(
    "/articles/syncCombinationArticles",
    {
      body: {
        articlesToAdd: toAdd,
        articlesToUpdate: toUpdate,
        articlesToDelete: toDelete,
      },
    }
  );
  await invalidateArticlesRelatedQueries(queryClient);
  return data;
}

async function invalidateArticlesRelatedQueries(queryClient?: QueryClient) {
  if (!queryClient) return;
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.Media],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.Medias],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.ArticlesByMedia],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.ArticlesByGroup],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.ArticlesByMediaAndProductArticleId],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.Notifications],
    }),
  ]);
}

export const getImageUsageByMediaId = async (
  mediaId: string
): Promise<ImageUsageByMediaResponse | null> => {
  type RawImageUsageResponse = {
    containsDeletedItems?: boolean;
    imagesWithDeletedItems?: ImageUsageEntry[];
    message?: string;
    dependentImages?: ImageUsageEntry[];
  };

  const normalize = (
    data: RawImageUsageResponse
  ): ImageUsageByMediaResponse => ({
    ...data,
    containsDeletedItems: !!data.containsDeletedItems,
    imagesWithDeletedItems: data.imagesWithDeletedItems ?? [],
    dependentImages: data.imagesWithDeletedItems ?? data.dependentImages ?? [],
  });

  try {
    const data = await httpClient.roomsettings.get<RawImageUsageResponse>(
      `/images/medias/${mediaId}/check-deleted-items`
    );
    return normalize(data ?? {});
  } catch (error) {
    if (error instanceof HttpError && error.status === 409) {
      return normalize((error.body as RawImageUsageResponse) ?? {});
    }
    console.error("Fetch error:", error);
  }
  return null;
};

export type Badge4AK1Response = {
  itemNo: string;
  types?: string[];
};

const normalizeItemNos = (itemNos: string) =>
  Array.from(
    new Set(
      itemNos
        .split(",")
        .map((itemNo) => itemNo.trim())
        .filter(Boolean)
    )
  ).join(",");

export const get4AK1BadgesData = async (
  itemNos: string,
  countryId: string
): Promise<Badge4AK1Response[]> => {
  const normalizedItemNos = normalizeItemNos(itemNos);
  if (!normalizedItemNos || !countryId) {
    return [];
  }

  try {
    const data = await httpClient.data.get<Badge4AK1Response[]>(
      "acesking/get-4a1k-type-for-articles-on-country",
      {
        params: {
          itemNos: normalizedItemNos,
          countryId,
        },
      }
    );
    return data || [];
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return [];
};

export const get4AK1BadgesByItemNo = async (
  itemNos: string,
  countryId: string
): Promise<Record<string, string[]>> => {
  const normalizedItemNos = normalizeItemNos(itemNos);
  const itemNoList = normalizedItemNos ? normalizedItemNos.split(",") : [];
  const badgesByItemNo = Object.fromEntries(
    itemNoList.map((itemNo) => [itemNo, [] as string[]])
  );

  const data = await get4AK1BadgesData(normalizedItemNos, countryId);
  data.forEach((item) => {
    const itemNo = item.itemNo.trim();
    if (itemNo) {
      badgesByItemNo[itemNo] = Array.from(
        new Set([...(badgesByItemNo[itemNo] ?? []), ...(item.types || [])])
      );
    }
  });

  return badgesByItemNo;
};

export const get4AK1Badges = async (
  itemNos: string,
  countryId: string
): Promise<string[]> => {
  const normalizedItemNos = normalizeItemNos(itemNos);
  const firstItemNo = normalizedItemNos.split(",")[0];
  if (!firstItemNo) {
    return [];
  }

  const badgesByItemNo = await get4AK1BadgesByItemNo(firstItemNo, countryId);
  return badgesByItemNo[firstItemNo] ?? [];
};

export type ItemTagsResponse = {
  itemNo: string;
  itemType: string;
  validFrom: string;
  validTo: string;
  tags?: string[];
};

export const getItemTagsData = async (
  itemNos: string
): Promise<ItemTagsResponse[]> => {
  try {
    const data = await httpClient.data.get<
      ItemTagsResponse | ItemTagsResponse[] | null
    >("item-tags/getItemTags", {
      params: {
        itemNos,
      },
    });

    if (!data) return [];

    return Array.isArray(data) ? data : [data];
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return [];
};

export const getItemTags = async (itemNos: string): Promise<string[]> => {
  const itemTags = await getItemTagsData(itemNos);
  return Array.from(new Set(itemTags.flatMap((item) => item.tags ?? [])));
};
